import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Checkbox,
  Dropdown,
  Field,
  Input,
  Option,
  Spinner,
  Switch,
  Text,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  TEMPLATE_LEDGER,
  type TemplateLedger,
  US_STATE_CODES,
  type AccountOverlay,
  type JournalOverlay,
  type Question,
  type QuestionContext,
} from '@lg/shared';
import { useApi } from '../api/client';

const useStyles = makeStyles({
  label: { display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  // Every track can shrink (minmax(0, …)); narrow screens drop to two columns.
  address: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1.4fr) minmax(0, 0.8fr) minmax(0, 0.9fr)',
    gap: tokens.spacingHorizontalM,
    '@media (max-width: 1100px)': { gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' },
    '& .fui-Input, & .fui-Dropdown': { minWidth: 0, width: '100%' },
  },
  fieldset: {
    border: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  legend: { padding: 0, marginBottom: tokens.spacingVerticalXS, fontWeight: tokens.fontWeightSemibold },
  narrowDropdown: { minWidth: 'unset', width: '100%' },
  fieldsetError: { color: tokens.colorPaletteRedForeground1, fontSize: tokens.fontSizeBase200 },
  hint: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  required: { color: tokens.colorPaletteRedForeground1 },
  checks: { display: 'flex', flexWrap: 'wrap', columnGap: tokens.spacingHorizontalL },
  vendors: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    maxHeight: '260px',
    overflowY: 'auto',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingHorizontalS,
  },
  // Overlay rows: one input per cell, labelled by the column header (aria-label).
  overlayGrid: {
    display: 'grid',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
    '& .fui-Input': { minWidth: 0, width: '100%' },
  },
  journals: { gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr) auto' },
  accounts: { gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto' },
  colHead: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  overlayActions: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
});

const asRows = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

/** Overlay B: new journal names, each limited to a main account range (journal control). */
function JournalRows({
  value,
  onChange,
  disabled,
  ledger,
}: {
  value: JournalOverlay[];
  onChange: (v: JournalOverlay[]) => void;
  disabled: boolean;
  ledger: TemplateLedger;
}) {
  const styles = useStyles();
  const set = (i: number, patch: Partial<JournalOverlay>) =>
    onChange(value.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const heads = ['Journal name', 'Description', 'From main account', 'To main account'];
  return (
    <>
      {value.length > 0 && (
        <div className={mergeClasses(styles.overlayGrid, styles.journals)}>
          {heads.map((h) => (
            <Text key={h} className={styles.colHead}>
              {h}
            </Text>
          ))}
          <span />
          {value.map((r, i) => (
            <div key={i} style={{ display: 'contents' }}>
              <Input
                aria-label={`Journal ${i + 1} name`}
                value={r.name}
                disabled={disabled}
                placeholder="RESTRICT"
                onChange={(_, d) => set(i, { name: d.value })}
              />
              <Input
                aria-label={`Journal ${i + 1} description`}
                value={r.description}
                disabled={disabled}
                placeholder="Restricted cash journal"
                onChange={(_, d) => set(i, { description: d.value })}
              />
              <Input
                aria-label={`Journal ${i + 1} from main account`}
                value={r.fromAccount}
                disabled={disabled}
                placeholder="110100"
                onChange={(_, d) => set(i, { fromAccount: d.value })}
              />
              <Input
                aria-label={`Journal ${i + 1} to main account`}
                value={r.toAccount}
                disabled={disabled}
                placeholder="110199"
                onChange={(_, d) => set(i, { toAccount: d.value })}
              />
              <Button
                appearance="subtle"
                disabled={disabled}
                aria-label={`Remove journal ${i + 1}`}
                onClick={() => onChange(value.filter((_, j) => j !== i))}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
      <div className={styles.overlayActions}>
        <Button
          disabled={disabled}
          onClick={() => onChange([...value, { name: '', description: '', fromAccount: '', toAccount: '' }])}
        >
          Add journal name
        </Button>
        {value.length === 0 && (
          <Button
            appearance="subtle"
            disabled={disabled}
            onClick={() => onChange([{ ...ledger.exampleJournal }])}
          >
            Use example: restricted cash journal
          </Button>
        )}
      </div>
      <Text className={styles.hint}>Template journals (unchanged): {ledger.journalNames.join(', ')}.</Text>
    </>
  );
}

/** Overlay C: main accounts suspended or given a default dimension in this legal entity only. */
function AccountRows({
  value,
  onChange,
  disabled,
  ledger,
}: {
  value: AccountOverlay[];
  onChange: (v: AccountOverlay[]) => void;
  disabled: boolean;
  ledger: TemplateLedger;
}) {
  const styles = useStyles();
  const dim = ledger.defaultDimension.name;
  const set = (i: number, patch: Partial<AccountOverlay>) =>
    onChange(value.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <>
      {value.length > 0 && (
        <div className={mergeClasses(styles.overlayGrid, styles.accounts)}>
          {['Main account', 'Suspend for this venue', `Default ${dim}`].map((h) => (
            <Text key={h} className={styles.colHead}>
              {h}
            </Text>
          ))}
          <span />
          {value.map((r, i) => (
            <div key={i} style={{ display: 'contents' }}>
              <Input
                aria-label={`Account ${i + 1} main account`}
                value={r.mainAccountId}
                disabled={disabled}
                placeholder="618160"
                onChange={(_, d) => set(i, { mainAccountId: d.value })}
              />
              <Switch
                aria-label={`Account ${i + 1} suspend for this venue`}
                checked={r.suspend}
                disabled={disabled}
                label={r.suspend ? 'Suspended' : 'Active'}
                onChange={(_, d) => set(i, { suspend: d.checked })}
              />
              <Input
                aria-label={`Account ${i + 1} default ${dim}`}
                value={r.defaultDimensionValue}
                disabled={disabled}
                placeholder="(none)"
                onChange={(_, d) => set(i, { defaultDimensionValue: d.value.toUpperCase() })}
              />
              <Button
                appearance="subtle"
                disabled={disabled}
                aria-label={`Remove account ${i + 1}`}
                onClick={() => onChange(value.filter((_, j) => j !== i))}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
      <div className={styles.overlayActions}>
        <Button
          disabled={disabled}
          onClick={() =>
            onChange([...value, { mainAccountId: '', suspend: true, defaultDimensionValue: '' }])
          }
        >
          Add main account
        </Button>
        {value.length === 0 && (
          <Button
            appearance="subtle"
            disabled={disabled}
            onClick={() =>
              onChange([
                { mainAccountId: '510360', suspend: true, defaultDimensionValue: '' },
                { mainAccountId: '618160', suspend: true, defaultDimensionValue: '' },
                { mainAccountId: '600500', suspend: false, defaultDimensionValue: '002' },
              ])
            }
          >
            Use example: suspend two, default {dim} on one
          </Button>
        )}
      </div>
    </>
  );
}

export interface QuestionFieldProps {
  question: Question;
  value: unknown;
  onChange: (value: unknown) => void;
  errors: string[];
  deviation: boolean;
  ctx: QuestionContext;
  disabled: boolean;
  /** Extra status line under the field (e.g. live ID availability) */
  status?: { state: 'checking' | 'ok' | 'error'; text: string } | null;
}

const asString = (v: unknown): string =>
  typeof v === 'string' ? v : v === undefined || v === null ? '' : String(v);
const asArray = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : []);

function VendorPicker({
  templateId,
  value,
  onChange,
  disabled,
}: {
  templateId: string;
  value: string[];
  onChange: (v: string[]) => void;
  disabled: boolean;
}) {
  const styles = useStyles();
  const api = useApi();
  const vendors = useQuery({
    queryKey: ['vendors', templateId],
    queryFn: () => api.listTemplateVendors(templateId),
  });
  if (vendors.isPending) return <Spinner size="tiny" label="Loading template vendors…" />;
  return (
    <div className={styles.vendors}>
      {(vendors.data ?? []).map((v) => (
        <Checkbox
          key={v.vendorId}
          label={`${v.vendorId} · ${v.name}`}
          checked={value.includes(v.vendorId)}
          disabled={disabled}
          onChange={(_, d) =>
            onChange(d.checked ? [...value, v.vendorId] : value.filter((x) => x !== v.vendorId))
          }
        />
      ))}
    </div>
  );
}

export function QuestionField({
  question: q,
  value,
  onChange,
  errors,
  deviation,
  ctx,
  disabled,
  status,
}: QuestionFieldProps) {
  const styles = useStyles();
  const options = q.options?.(ctx) ?? [];
  const label = (
    <span className={styles.label}>
      {q.label}
      {deviation && (
        <Badge size="small" appearance="tint" color="warning">
          Differs from template
        </Badge>
      )}
    </span>
  );
  const validationState = errors.length
    ? 'error'
    : status?.state === 'ok'
      ? 'success'
      : status?.state === 'error'
        ? 'error'
        : 'none';
  const validationMessage = errors.length ? errors.join('; ') : status?.text;

  // Several controls under one label: a Fluent Field would give them all the
  // same id (so its label toggles the wrong checkbox), so use a fieldset.
  const group = (children: JSX.Element): JSX.Element => (
    <fieldset className={styles.fieldset}>
      <legend className={styles.legend}>
        {label}
        {(typeof q.required === 'boolean' ? q.required : false) && (
          <span className={styles.required}> *</span>
        )}
      </legend>
      {q.help && <Text className={styles.hint}>{q.help}</Text>}
      {children}
      {errors.length > 0 && <Text className={styles.fieldsetError}>{errors.join('; ')}</Text>}
    </fieldset>
  );

  let control: JSX.Element;
  switch (q.type) {
    case 'date':
      control = (
        <Input
          type="date"
          value={asString(value)}
          disabled={disabled}
          onChange={(_, d) => onChange(d.value)}
        />
      );
      break;
    case 'choice': {
      const selected = options.find((o) => o.value === value);
      control = (
        <Dropdown
          value={selected?.label ?? ''}
          selectedOptions={selected ? [selected.value] : []}
          disabled={disabled}
          placeholder="Select…"
          onOptionSelect={(_, d) => onChange(d.optionValue)}
        >
          {options.map((o) => (
            <Option key={o.value} value={o.value}>
              {o.label}
            </Option>
          ))}
        </Dropdown>
      );
      break;
    }
    case 'multi':
    case 'workflows': {
      const arr = asArray(value);
      return group(
        <div className={styles.checks}>
          {options.map((o) => (
            <Checkbox
              key={o.value}
              label={o.label}
              checked={arr.includes(o.value)}
              disabled={disabled}
              onChange={(_, d) => onChange(d.checked ? [...arr, o.value] : arr.filter((x) => x !== o.value))}
            />
          ))}
        </div>,
      );
    }
    case 'yesno':
      control = (
        <Switch
          checked={value === true}
          disabled={disabled}
          label={value === true ? 'Yes' : value === false ? 'No' : 'Not answered'}
          onChange={(_, d) => onChange(d.checked)}
        />
      );
      break;
    case 'address': {
      // Four labelled sub-fields; a single Field cannot label several controls.
      const a = (value && typeof value === 'object' ? value : {}) as Record<string, string>;
      const set = (k: string, v: string) => onChange({ ...a, [k]: v });
      return group(
        <div className={styles.address}>
          <Field label="Street" required>
            <Input
              value={a['street'] ?? ''}
              disabled={disabled}
              onChange={(_, d) => set('street', d.value)}
            />
          </Field>
          <Field label="City" required>
            <Input value={a['city'] ?? ''} disabled={disabled} onChange={(_, d) => set('city', d.value)} />
          </Field>
          <Field label="State" required>
            <Dropdown
              className={styles.narrowDropdown}
              value={a['state'] ?? ''}
              selectedOptions={a['state'] ? [a['state']] : []}
              disabled={disabled}
              onOptionSelect={(_, d) => set('state', d.optionValue ?? '')}
            >
              {US_STATE_CODES.map((s) => (
                <Option key={s} value={s}>
                  {s}
                </Option>
              ))}
            </Dropdown>
          </Field>
          <Field label="ZIP" required>
            <Input value={a['zip'] ?? ''} disabled={disabled} onChange={(_, d) => set('zip', d.value)} />
          </Field>
        </div>,
      );
    }
    case 'journals':
      return group(
        <JournalRows
          value={asRows<JournalOverlay>(value)}
          onChange={onChange}
          disabled={disabled}
          ledger={ctx.ledger ?? TEMPLATE_LEDGER}
        />,
      );
    case 'accountOverrides':
      return group(
        <AccountRows
          value={asRows<AccountOverlay>(value)}
          onChange={onChange}
          disabled={disabled}
          ledger={ctx.ledger ?? TEMPLATE_LEDGER}
        />,
      );
    case 'vendors':
      return group(
        <VendorPicker
          templateId={ctx.template.id}
          value={asArray(value)}
          onChange={onChange}
          disabled={disabled}
        />,
      );
    default:
      control = (
        <Input
          value={asString(value)}
          disabled={disabled}
          type={q.type === 'user' ? 'email' : 'text'}
          placeholder={q.sensitive ? '12-3456789' : undefined}
          onChange={(_, d) => onChange(q.key === 'entity.id' ? d.value.toUpperCase() : d.value)}
        />
      );
  }

  return (
    <Field
      label={label}
      required={typeof q.required === 'boolean' ? q.required : undefined}
      hint={q.help}
      validationState={validationState}
      validationMessage={status?.state === 'checking' ? 'Checking availability…' : validationMessage}
    >
      {control}
    </Field>
  );
}
